import { useEffect, useRef, useState } from 'react';

export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        setMousePosition({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
      }
    };

    const hero = heroRef.current;
    if (hero) {
      hero.addEventListener('mousemove', handleMouseMove);
      hero.addEventListener('mouseenter', () => setIsHovering(true));
      hero.addEventListener('mouseleave', () => setIsHovering(false));
    }

    return () => {
      if (hero) {
        hero.removeEventListener('mousemove', handleMouseMove);
      }
    };
  }, []);

  const scrollToLeadCapture = () => {
    const leadCaptureSection = document.getElementById('lead-capture-section');
    if (leadCaptureSection) {
      leadCaptureSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{
        background: 'radial-gradient(circle at 50% 50%, #1a0b2e 0%, #0a0a0f 100%)',
      }}
    >
      {/* Animated Background Grid */}
      <div className="absolute inset-0 opacity-20">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(rgba(139, 92, 246, 0.1) 1px, transparent 1px),
                             linear-gradient(90deg, rgba(139, 92, 246, 0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
            animation: 'gridMove 20s linear infinite',
          }}
        />
      </div>

      {/* Lens Spotlight Effect */}
      {isHovering && (
        <div
          className="absolute pointer-events-none transition-opacity duration-300"
          style={{
            left: mousePosition.x,
            top: mousePosition.y,
            width: '600px',
            height: '600px',
            transform: 'translate(-50%, -50%)',
            background: 'radial-gradient(circle, rgba(139, 92, 246, 0.15) 0%, transparent 70%)',
            filter: 'blur(40px)',
          }}
        />
      )}

      {/* Floating Particles */}
      <div className="absolute inset-0">
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-purple-400 rounded-full opacity-40"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${5 + Math.random() * 10}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`,
            }}
          />
        ))}
      </div>

      {/* 3D Floating Elements */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative w-full max-w-4xl h-96">
          {/* Central Cube */}
          <div
            className="absolute left-1/2 top-1/2 w-32 h-32 -translate-x-1/2 -translate-y-1/2"
            style={{
              animation: 'float3d 6s ease-in-out infinite',
              transformStyle: 'preserve-3d',
            }}
          >
            <div
              className="w-full h-full border-2 border-purple-500/30 rounded-lg"
              style={{
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(59, 130, 246, 0.1))',
                boxShadow: '0 0 60px rgba(139, 92, 246, 0.3)',
                transform: 'rotateX(45deg) rotateY(45deg)',
                animation: 'rotate3d 20s linear infinite',
              }}
            />
          </div>

          {/* Orbiting Nodes */}
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="absolute left-1/2 top-1/2 w-16 h-16 -translate-x-1/2 -translate-y-1/2"
              style={{
                animation: `orbit ${8 + i * 2}s linear infinite`,
                animationDelay: `${i * 0.5}s`,
              }}
            >
              <div className="w-full h-full bg-gradient-to-br from-cyan-400 to-blue-600 rounded-lg opacity-60 blur-sm" />
            </div>
          ))}

          {/* Connection Lines */}
          <svg className="absolute inset-0 w-full h-full opacity-30">
            {[...Array(8)].map((_, i) => (
              <line
                key={i}
                x1="50%"
                y1="50%"
                x2={`${50 + Math.cos((i * Math.PI) / 4) * 40}%`}
                y2={`${50 + Math.sin((i * Math.PI) / 4) * 40}%`}
                stroke="url(#gradient)"
                strokeWidth="1"
                className="animate-pulse"
                style={{ animationDelay: `${i * 0.2}s` }}
              />
            ))}
            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#8b5cf6" />
                <stop offset="100%" stopColor="#3b82f6" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
        <div
          className="mb-8 opacity-0"
          style={{ animation: 'fadeInUp 1s ease-out 0.2s forwards' }}
        >
          <h1 className="text-7xl md:text-8xl font-black mb-6 leading-tight">
            <span
              className="bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent"
              style={{
                textShadow: '0 0 80px rgba(139, 92, 246, 0.5)',
              }}
            >
              AUTOMATE
            </span>
            <br />
            <span className="text-white">EVERYTHING.</span>
          </h1>
        </div>

        <div
          className="mb-6 opacity-0"
          style={{ animation: 'fadeInUp 1s ease-out 0.4s forwards' }}
        >
          <p className="text-2xl md:text-3xl font-light text-gray-300 mb-4">
            AI + Automation + Integration for the Modern Enterprise.
          </p>
          <p className="text-xl text-purple-300 font-medium">
            AI Agents &amp; Smart Integrations with Real Business Impact.
          </p>
        </div>

        <div
          className="opacity-0"
          style={{ animation: 'fadeInUp 1s ease-out 0.6s forwards' }}
        >
          <button
            onClick={scrollToLeadCapture}
            className="group relative px-12 py-5 text-lg font-bold text-white rounded-full overflow-hidden transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            style={{
              background: 'linear-gradient(135deg, #8b5cf6, #3b82f6)',
              boxShadow: '0 0 40px rgba(139, 92, 246, 0.5)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 0 60px rgba(139, 92, 246, 0.8)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 0 40px rgba(139, 92, 246, 0.5)';
            }}
          >
            <span className="relative z-10">Get a Free Automation Audit</span>
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }

        @keyframes float3d {
          0%, 100% {
            transform: translateY(0px) translateZ(0px);
          }
          50% {
            transform: translateY(-30px) translateZ(50px);
          }
        }

        @keyframes rotate3d {
          from {
            transform: rotateX(45deg) rotateY(45deg) rotateZ(0deg);
          }
          to {
            transform: rotateX(45deg) rotateY(45deg) rotateZ(360deg);
          }
        }

        @keyframes orbit {
          from {
            transform: translate(-50%, -50%) rotate(0deg) translateX(150px) rotate(0deg);
          }
          to {
            transform: translate(-50%, -50%) rotate(360deg) translateX(150px) rotate(-360deg);
          }
        }

        @keyframes gridMove {
          from {
            transform: translateY(0);
          }
          to {
            transform: translateY(50px);
          }
        }

        @keyframes scrollDown {
          0% {
            transform: translateY(0);
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
          100% {
            transform: translateY(12px);
            opacity: 0;
          }
        }
      `}</style>
    </section>
  );
}
